(function () {
  if (window.__collectionAutoMarqueeBound) {
    return;
  }
  window.__collectionAutoMarqueeBound = true;

  const ROOT_SELECTOR = '[data-collection-auto-marquee]';
  const VIEWPORT_SELECTOR = '[data-collection-marquee]';
  const PIXELS_PER_SECOND = 60;
  const DRAG_THRESHOLD_PX = 5;
  const MIN_COPIES = 2;
  const GROUP_SELECTOR = '[data-marquee-group]';
  const states = new WeakMap();
  const activeViewports = new Set();
  let rafId = 0;
  let lastTime = 0;
  let resizeObserver = null;

  /**
   * @returns {boolean}
   */
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * @param {number} offset
   * @param {number} loopWidth
   * @returns {number}
   */
  function wrapOffset(offset, loopWidth) {
    if (loopWidth <= 0) {
      return 0;
    }

    let nextOffset = offset % loopWidth;
    if (nextOffset > 0) {
      nextOffset -= loopWidth;
    }
    return nextOffset;
  }

  /**
   * @param {object} state
   */
  function applyTransform(state) {
    state.offset = wrapOffset(state.offset, state.loopWidth);
    state.track.style.transform = 'translate3d(' + state.offset + 'px, 0, 0)';
  }

  /**
   * @param {HTMLElement} viewport
   * @returns {object | null}
   */
  function getState(viewport) {
    return states.get(viewport) || null;
  }

  /**
   * @param {number} time
   */
  function tick(time) {
    const delta = lastTime ? Math.min((time - lastTime) / 1000, 0.05) : 0;
    lastTime = time;

    activeViewports.forEach(function (viewport) {
      const state = getState(viewport);
      if (!state || state.isPressed || state.isDragging || prefersReducedMotion()) {
        return;
      }

      const direction = state.direction === 'right' ? 1 : -1;
      state.offset += direction * PIXELS_PER_SECOND * delta;
      applyTransform(state);
    });

    rafId = window.requestAnimationFrame(tick);
  }

  function ensureTick() {
    if (!rafId) {
      lastTime = 0;
      rafId = window.requestAnimationFrame(tick);
    }
  }

  /**
   * @param {HTMLElement} viewport
   */
  function bindViewport(viewport) {
    if (viewport.dataset.marqueeBound === 'true') {
      return;
    }
    viewport.dataset.marqueeBound = 'true';

    function stopPointerTracking() {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerCancel);
    }

    /**
     * Movement under the threshold stays a click so the card link can navigate.
     * Past the threshold, the same gesture scrolls the row and the following click is cancelled.
     * @param {PointerEvent} event
     */
    function onPointerMove(event) {
      const state = getState(viewport);
      if (!state || !state.isPressed || event.pointerId !== state.pointerId) {
        return;
      }

      if (!state.isDragging) {
        if (Math.abs(event.clientX - state.dragStartX) < DRAG_THRESHOLD_PX) {
          return;
        }
        state.isDragging = true;
        state.didDrag = true;
        viewport.classList.add('is-dragging');
      }

      const deltaX = event.clientX - state.lastX;
      state.lastX = event.clientX;
      state.offset += deltaX;
      applyTransform(state);
      event.preventDefault();
    }

    /**
     * Follow the pressed card when the row moved and the browser will not emit a click on that link.
     * A release still on the same link is left to the native click, including modifier keys.
     * @param {PointerEvent} event
     * @param {EventTarget | null} pressTarget
     */
    function followPressedLink(event, pressTarget) {
      if (!pressTarget || !pressTarget.closest) {
        return;
      }

      const link = pressTarget.closest('a[href]');
      if (!link || !link.isConnected || !viewport.contains(link)) {
        return;
      }

      const releasedLink = event.target && event.target.closest ? event.target.closest('a[href]') : null;
      if (releasedLink === link) {
        return;
      }
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) {
        return;
      }
      if (link.protocol !== 'http:' && link.protocol !== 'https:') {
        return;
      }

      window.location.assign(link.href);
    }

    /**
     * @param {PointerEvent} event
     * @param {boolean} allowNavigate
     */
    function releasePointer(event, allowNavigate) {
      const state = getState(viewport);
      if (!state || !state.isPressed || event.pointerId !== state.pointerId) {
        return;
      }

      const dragged = state.didDrag;
      const pressTarget = state.pressTarget;
      state.pressTarget = null;
      state.isPressed = false;
      state.isDragging = false;
      viewport.classList.remove('is-dragging');
      stopPointerTracking();

      if (allowNavigate && !dragged) {
        followPressedLink(event, pressTarget);
      }
    }

    /**
     * @param {PointerEvent} event
     */
    function onPointerUp(event) {
      releasePointer(event, true);
    }

    /**
     * @param {PointerEvent} event
     */
    function onPointerCancel(event) {
      releasePointer(event, false);
    }

    viewport.addEventListener('pointerdown', function (event) {
      const state = getState(viewport);
      if (!state || event.button !== 0 || state.isPressed) {
        return;
      }

      state.isPressed = true;
      state.isDragging = false;
      state.didDrag = false;
      state.pointerId = event.pointerId;
      state.pressTarget = event.target;
      state.dragStartX = event.clientX;
      state.lastX = event.clientX;
      document.addEventListener('pointermove', onPointerMove, { passive: false });
      document.addEventListener('pointerup', onPointerUp);
      document.addEventListener('pointercancel', onPointerCancel);
    });

    viewport.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });

    viewport.addEventListener(
      'click',
      function (event) {
        const state = getState(viewport);
        if (!state || !state.didDrag) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        state.didDrag = false;
      },
      true
    );
  }

  /**
   * Distance from one group to the next. That span is one loop.
   * @param {HTMLElement} group
   * @returns {number}
   */
  function measureGroupStride(group) {
    const next = group.nextElementSibling;
    if (next && next.matches(GROUP_SELECTOR)) {
      const stride = next.getBoundingClientRect().left - group.getBoundingClientRect().left;
      if (stride > 0) {
        return stride;
      }
    }
    return group.getBoundingClientRect().width;
  }

  /**
   * Copies required so the viewport stays full while the track shifts by one group.
   * The extra pixel avoids a hairline gap when the widths divide evenly.
   * @param {number} viewportWidth
   * @param {number} groupWidth
   * @returns {number}
   */
  function copiesNeededFor(viewportWidth, groupWidth) {
    return Math.max(MIN_COPIES, Math.ceil((viewportWidth + 1) / groupWidth) + 1);
  }

  /**
   * Clones stay clickable so the card on screen can open its collection.
   * aria-hidden and tabindex=-1 keep duplicates out of the tab order.
   * inert is omitted because it disables hit testing on the visible copy.
   * @param {HTMLElement} group
   */
  function markGroupAsClone(group) {
    group.setAttribute('aria-hidden', 'true');
    group.removeAttribute('inert');
    const links = group.querySelectorAll('a[href]');
    for (let index = 0; index < links.length; index++) {
      links[index].setAttribute('tabindex', '-1');
    }
  }

  /**
   * Repeat the source group until the row covers the viewport plus one loop.
   * @param {HTMLElement} viewport
   * @param {object} state
   */
  function syncGroupCopies(viewport, state) {
    const track = state.track;
    let groups = track.querySelectorAll(GROUP_SELECTOR);
    const source = groups[0];
    if (!source) {
      state.loopWidth = 0;
      return;
    }

    const groupWidth = measureGroupStride(source);
    const viewportWidth = viewport.clientWidth;
    if (groupWidth <= 0 || viewportWidth <= 0) {
      state.loopWidth = 0;
      return;
    }

    const copiesNeeded = copiesNeededFor(viewportWidth, groupWidth);
    if (groups.length > copiesNeeded) {
      for (let index = groups.length - 1; index >= copiesNeeded; index--) {
        groups[index].remove();
      }
    } else {
      for (let index = groups.length; index < copiesNeeded; index++) {
        const clone = source.cloneNode(true);
        markGroupAsClone(clone);
        track.appendChild(clone);
      }
    }

    groups = track.querySelectorAll(GROUP_SELECTOR);
    for (let index = 1; index < groups.length; index++) {
      markGroupAsClone(groups[index]);
    }

    state.loopWidth = measureGroupStride(groups[0]);
  }

  /**
   * @param {HTMLElement} viewport
   */
  function watchViewport(viewport) {
    if (!resizeObserver || viewport.dataset.marqueeObserved === 'true') {
      return;
    }
    viewport.dataset.marqueeObserved = 'true';
    resizeObserver.observe(viewport);
  }

  /**
   * @param {HTMLElement} viewport
   */
  function prepareViewport(viewport) {
    watchViewport(viewport);

    if (window.getComputedStyle(viewport).display === 'none') {
      activeViewports.delete(viewport);
      return;
    }

    const track = viewport.querySelector('[data-marquee-track]');
    const group = viewport.querySelector(GROUP_SELECTOR);
    if (!track || !group) {
      return;
    }

    let state = getState(viewport);
    if (!state) {
      state = {
        track: track,
        direction: viewport.dataset.marqueeDirection === 'right' ? 'right' : 'left',
        offset: 0,
        loopWidth: 0,
        isPressed: false,
        isDragging: false,
        didDrag: false,
        pointerId: 0,
        pressTarget: null,
        dragStartX: 0,
        lastX: 0,
      };
      states.set(viewport, state);
      bindViewport(viewport);
    } else {
      state.track = track;
    }

    syncGroupCopies(viewport, state);
    if (state.loopWidth <= 0) {
      activeViewports.delete(viewport);
      return;
    }

    applyTransform(state);
    activeViewports.add(viewport);
    ensureTick();
  }

  /**
   * @param {ParentNode} [scope]
   */
  function initAll(scope) {
    const rootNodes = (scope || document).querySelectorAll(ROOT_SELECTOR);
    rootNodes.forEach(function (root) {
      root.querySelectorAll(VIEWPORT_SELECTOR).forEach(prepareViewport);
    });
  }

  function start() {
    initAll(document);
  }

  if (typeof ResizeObserver === 'function') {
    resizeObserver = new ResizeObserver(function (entries) {
      entries.forEach(function (entry) {
        prepareViewport(entry.target);
      });
    });
  } else {
    window.addEventListener('resize', start);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.addEventListener('load', start);
  window.matchMedia('(min-width: 750px)').addEventListener('change', start);
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    if (!event.target || !event.target.querySelectorAll) {
      return;
    }
    event.target.querySelectorAll(VIEWPORT_SELECTOR).forEach(function (viewport) {
      activeViewports.delete(viewport);
      if (resizeObserver) {
        resizeObserver.unobserve(viewport);
      }
    });
  });
})();
